import { FC } from 'react';
import { ExternalLink } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import { Input } from '../../ui/Input';
import { AffiliateSettings } from '../../../utils/affiliateSettings';

interface BrandingSettingsTabProps {
  affiliateSettings: AffiliateSettings;
  setAffiliateSettings: React.Dispatch<React.SetStateAction<AffiliateSettings>>;
  isSaving: boolean;
  onSave: () => void;
}

export const BrandingSettingsTab: FC<BrandingSettingsTabProps> = ({
  affiliateSettings,
  setAffiliateSettings,
  isSaving,
  onSave,
}) => {
  const showcaseUrl = `/showcase/${affiliateSettings.tenantSlug || 'default'}`;

  return (
    <Card id="visual-identity-settings">
      <Card.Header
        title={
          <div>
            <h3 className="text-sm font-semibold text-content-main">
              Identidade Visual & Vitrine do Canal
            </h3>
            <p className="text-xs text-content-muted">
              Personalização da marca, logotipo, cores institucionais e
              metadados públicos
            </p>
          </div>
        }
        action={
          <div className="flex items-center gap-2">
            <a
              href={showcaseUrl}
              target="_blank"
              rel="noreferrer"
              className="hover:bg-surface-border/50 inline-flex items-center gap-1.5 rounded-xl border border-surface-border bg-surface-subtle px-3 py-1.5 text-xs font-semibold text-content-main transition-colors"
            >
              <span>Ver Vitrine Pública</span>
              <ExternalLink className="h-3.5 w-3.5 text-content-muted" />
            </a>
            <Button
              variant="primary"
              size="sm"
              isLoading={isSaving}
              onClick={onSave}
            >
              Salvar Identidade
            </Button>
          </div>
        }
      />
      <Card.Body className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Formulário de Branding */}
          <div className="space-y-4 lg:col-span-7">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Nome da Operação (Painel)"
                value={affiliateSettings.projectName || ''}
                onChange={(e) =>
                  setAffiliateSettings({
                    ...affiliateSettings,
                    projectName: e.target.value,
                  })
                }
                placeholder="Ex: Achadinhos Tech"
                helperText="Exibido internamente no cabeçalho operacional"
              />
              <Input
                label="Título Público do Canal"
                value={affiliateSettings.publicTitle || ''}
                onChange={(e) =>
                  setAffiliateSettings({
                    ...affiliateSettings,
                    publicTitle: e.target.value,
                  })
                }
                placeholder="Ex: Achadinhos Tech VIP"
                helperText="Exibido na vitrine e cabeçalho público"
              />
            </div>

            <Input
              label="Slogan / Apresentação do Canal"
              value={affiliateSettings.slogan || ''}
              onChange={(e) =>
                setAffiliateSettings({
                  ...affiliateSettings,
                  slogan: e.target.value,
                })
              }
              placeholder="Ex: As melhores ofertas e promoções da internet em tempo real."
            />

            <Input
              label="URL do Avatar / Logo da Marca"
              value={affiliateSettings.logoUrl || ''}
              onChange={(e) =>
                setAffiliateSettings({
                  ...affiliateSettings,
                  logoUrl: e.target.value.trim(),
                })
              }
              placeholder="https://sua-empresa.com/logo.png"
              helperText="Link direto para imagem PNG ou SVG transparente"
            />

            {/* Seletores de Paleta de Cores */}
            <div className="grid grid-cols-1 gap-3 pt-1 sm:grid-cols-2">
              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-content-main">
                  Cor Primária (Destaques e Botões)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Seletor de cor primária"
                    value={affiliateSettings.primaryColor || '#2563EB'}
                    onChange={(e) =>
                      setAffiliateSettings({
                        ...affiliateSettings,
                        primaryColor: e.target.value,
                      })
                    }
                    className="h-10 w-12 cursor-pointer rounded-xl border border-surface-border bg-transparent p-1"
                  />
                  <Input
                    containerClassName="flex-1"
                    value={affiliateSettings.primaryColor || '#2563EB'}
                    onChange={(e) =>
                      setAffiliateSettings({
                        ...affiliateSettings,
                        primaryColor: e.target.value,
                      })
                    }
                    placeholder="#2563EB"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-xs font-semibold text-content-main">
                  Cor Secundária (Acentos e Badges)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    aria-label="Seletor de cor secundária"
                    value={affiliateSettings.secondaryColor || '#10B981'}
                    onChange={(e) =>
                      setAffiliateSettings({
                        ...affiliateSettings,
                        secondaryColor: e.target.value,
                      })
                    }
                    className="h-10 w-12 cursor-pointer rounded-xl border border-surface-border bg-transparent p-1"
                  />
                  <Input
                    containerClassName="flex-1"
                    value={affiliateSettings.secondaryColor || '#10B981'}
                    onChange={(e) =>
                      setAffiliateSettings({
                        ...affiliateSettings,
                        secondaryColor: e.target.value,
                      })
                    }
                    placeholder="#10B981"
                  />
                </div>
              </div>
            </div>

            <div className="pt-1">
              <Input
                label="Alerta de Validade na Vitrine (dias)"
                type="number"
                min={1}
                max={30}
                value={
                  affiliateSettings.showcaseExpiryWarningDays !== undefined
                    ? String(affiliateSettings.showcaseExpiryWarningDays)
                    : '2'
                }
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  setAffiliateSettings({
                    ...affiliateSettings,
                    showcaseExpiryWarningDays: isNaN(val)
                      ? 2
                      : Math.max(1, val),
                  });
                }}
                helperText="Ofertas enviadas há mais tempo que esse período exibirão o aviso 'Preço e estoque sujeitos a alteração'"
              />
            </div>
          </div>

          {/* Live Preview Card */}
          <div className="flex flex-col justify-between rounded-2xl border border-surface-border bg-surface-canvas p-5 lg:col-span-5">
            <div>
              <div className="flex items-center justify-between pb-3">
                <span className="text-xs font-semibold text-content-main">
                  Pré-visualização em Tempo Real
                </span>
                <Badge variant="online" className="text-micro">
                  Página Pública
                </Badge>
              </div>

              {/* Banner de Demonstração */}
              <div className="mt-2 rounded-xl border border-surface-border bg-surface-subtle p-4 shadow-sm">
                <div className="flex items-center gap-3">
                  {affiliateSettings.logoUrl ? (
                    <img
                      src={affiliateSettings.logoUrl}
                      alt="Logo Preview"
                      className="h-12 w-12 rounded-xl border border-surface-border object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <div
                      className="shadow-xs flex h-12 w-12 items-center justify-center rounded-xl font-bold text-white"
                      style={{
                        backgroundColor:
                          affiliateSettings.primaryColor || '#2563EB',
                      }}
                    >
                      {(affiliateSettings.publicTitle || 'A')
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-bold text-content-main">
                      {affiliateSettings.publicTitle || 'Achadinhos Tech'}
                    </h4>
                    <p className="mt-0.5 line-clamp-2 text-micro text-content-muted">
                      {affiliateSettings.slogan ||
                        'As melhores ofertas e promoções da internet em tempo real.'}
                    </p>
                  </div>
                </div>

                {/* Exemplo de card de oferta renderizado no preview */}
                <div className="mt-4 rounded-lg border border-surface-border bg-surface-panel p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-micro font-medium text-content-muted">
                      Super Oferta Demonstrativa
                    </span>
                    <span
                      className="rounded-md px-1.5 py-0.5 text-micro font-bold text-white"
                      style={{
                        backgroundColor:
                          affiliateSettings.secondaryColor || '#10B981',
                      }}
                    >
                      -42% OFF
                    </span>
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-xs text-content-muted line-through">
                      R$ 299,00
                    </span>
                    <span className="text-sm font-extrabold text-content-main">
                      R$ 173,42
                    </span>
                  </div>
                  <button
                    type="button"
                    className="shadow-xs mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-semibold text-white"
                    style={{
                      backgroundColor:
                        affiliateSettings.primaryColor || '#2563EB',
                    }}
                  >
                    Pegar Promoção
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between border-t border-surface-border pt-3">
              <span className="text-micro text-content-muted">
                Simulação do cabeçalho da Vitrine
              </span>
              <span className="font-mono text-2xs text-content-muted">
                {affiliateSettings.primaryColor || '#2563EB'}
              </span>
            </div>
          </div>
        </div>
      </Card.Body>
      <Card.Footer>
        <div className="flex w-full items-center justify-end">
          <Button
            variant="primary"
            size="sm"
            isLoading={isSaving}
            onClick={onSave}
          >
            Salvar Identidade Visual
          </Button>
        </div>
      </Card.Footer>
    </Card>
  );
};
